import { Injector, ResourceRef, ResourceStatus, ValueEqualityFn } from '@angular/core';
import { filter, Observable, of, switchMap, throwError } from 'rxjs';
import { rxResource } from '@angular/core/rxjs-interop';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { Pack } from './pack.model';

export type CuePackLoaderParams<R> = {
  params: NoInfer<Exclude<R, undefined>>;
  abortSignal: AbortSignal;
  previous: {
    status: ResourceStatus;
  };
};

export type CuePackResourceOptions<TResult, R, TError = unknown> = {
  stream: (params: CuePackLoaderParams<R>) => Observable<Pack<TResult>>;
  params?: () => R;
  onLoaded?: (data: TResult) => void;
  defaultValue?: NoInfer<Pack<TResult>>;
  equal?: ValueEqualityFn<Pack<TResult>>;
  onError?: (error: TError) => void;
  injector?: Injector;
};

export function cuePackResource<TResult, R, TError = unknown>(
  opts: CuePackResourceOptions<TResult, R, TError>
): ResourceRef<TResult | undefined> {
  const resource = rxResource({
    stream: (params) =>
      opts.stream(params).pipe(
        filter((x) => x?.loading !== true),
        switchMap((response) => {
          if (!response.error) {
            return of(response.data);
          } else {
            return throwError(() => response.error);
          }
        })
      ) as any,
    equal: opts.equal,
    injector: opts.injector,
    params: opts.params,
  }) as ResourceRef<TResult>;

  if (opts.onLoaded || opts.onError) {
    explicitEffect([resource.status], ([status]) => {
      if (status == 'resolved') {
        opts.onLoaded?.(resource.value()!);
      }

      if (status == 'error') {
        opts.onError?.(resource.error() as TError);
      }
    });
  }

  return resource;
}
